import { uid } from '@tomo/core';
import { Store, useStore } from '../lib/store.ts';
import { kvGet, kvSet } from './db.ts';

/**
 * Who is using Tomo on this device. Without an account this is a local profile (free tier);
 * after signing in it becomes the account so votes/authorship line up across devices.
 */
export interface Identity {
  id: string;
  name: string;
  color: string;
  kind: 'local' | 'account';
  email?: string;
  /** The device's local profile id (kept so local data can be migrated to the account). */
  localId: string;
}

export const PERSON_COLORS = ['#F97066', '#F6A04D', '#EFB649', '#7ED69B', '#34C3B5', '#60A5FA', '#8B7CF6', '#C084FC', '#F472B6', '#A3A7B5'];

interface LocalProfile {
  id: string;
  name: string;
  color: string;
}

const PROFILE_KEY = 'profile';

export const identityStore = new Store<Identity>({ id: 'pending', name: 'You', color: PERSON_COLORS[2]!, kind: 'local', localId: 'pending' });

let localProfile: LocalProfile | null = null;

export async function loadIdentity(): Promise<Identity> {
  localProfile = (await kvGet<LocalProfile>(PROFILE_KEY)) ?? null;
  if (!localProfile) {
    localProfile = { id: `local-${uid(10)}`, name: 'You', color: PERSON_COLORS[Math.floor(Math.random() * PERSON_COLORS.length)]! };
    await kvSet(PROFILE_KEY, localProfile);
  }
  const current = identityStore.get();
  if (current.kind !== 'account') identityStore.set({ ...localProfile, kind: 'local', localId: localProfile.id });
  return identityStore.get();
}

export async function updateLocalProfile(patch: Partial<Pick<LocalProfile, 'name' | 'color'>>) {
  if (!localProfile) await loadIdentity();
  localProfile = { ...localProfile!, ...patch };
  await kvSet(PROFILE_KEY, localProfile);
  if (identityStore.get().kind === 'local') identityStore.set({ ...localProfile, kind: 'local', localId: localProfile.id });
}

/** Switch to a signed-in account (or back to the local profile with `null`). */
export function setAccountIdentity(account: { id: string; name: string; color: string; email: string } | null) {
  const localId = localProfile?.id ?? identityStore.get().localId;
  if (account) identityStore.set({ ...account, kind: 'account', localId });
  else if (localProfile) identityStore.set({ ...localProfile, kind: 'local', localId: localProfile.id });
}

export const getIdentity = () => identityStore.get();
export const useIdentity = () => useStore(identityStore);
